import React from 'react';
import { cn } from '../../utils/cn';

/**
 * One design system, different character per domain.
 *
 * Each product area gets its own header treatment so a student can tell at a
 * glance whether they are in an academic, commercial, financial or community
 * surface — without any of them leaving the Campuzon visual language. The
 * differentiation is carried by accent, surface and density, never by a second
 * set of components.
 */
export type Domain = 'academics' | 'marketplace' | 'work' | 'social' | 'housing' | 'money' | 'admin';
const TONES: Record<Domain, {
  rail: string;
  surface: string;
  eyebrow: string;
  measure: string;
}> = {
  // Structured, quiet, information-first.
  academics: {
    rail: 'bg-ink',
    surface: 'bg-surface',
    eyebrow: 'text-ink-muted',
    measure: 'max-w-[70ch]'
  },
  // Commercial and visual — warmer, more open.
  marketplace: {
    rail: 'bg-warn-700',
    surface: 'bg-warn-50',
    eyebrow: 'text-warn-800',
    measure: 'max-w-[62ch]'
  },
  // Professional, application-oriented.
  work: {
    rail: 'bg-info-700',
    surface: 'bg-info-50',
    eyebrow: 'text-info-800',
    measure: 'max-w-[62ch]'
  },
  // Conversational and community-driven.
  social: {
    rail: 'bg-brand-600',
    surface: 'bg-brand-50',
    eyebrow: 'text-brand-800',
    measure: 'max-w-[62ch]'
  },
  // Trust-focused and practical.
  housing: {
    rail: 'bg-brand-700',
    surface: 'bg-surface',
    eyebrow: 'text-brand-800',
    measure: 'max-w-[66ch]'
  },
  // Precise and financially restrained — no decoration at all.
  money: {
    rail: 'bg-ink',
    surface: 'bg-surface',
    eyebrow: 'text-ink-muted',
    measure: 'max-w-[66ch]'
  },
  // Dense and operational.
  admin: {
    rail: 'bg-ink-muted',
    surface: 'bg-surface',
    eyebrow: 'text-ink-muted',
    measure: 'max-w-[74ch]'
  }
};
interface DomainHeaderProps {
  domain: Domain;
  eyebrow: string;
  title: string;
  description?: string;
  actions?: React.ReactNode;
  /** Status, counts or standing that belong beside the title rather than below it. */
  meta?: React.ReactNode;
}
export function DomainHeader({
  domain,
  eyebrow,
  title,
  description,
  actions,
  meta
}: DomainHeaderProps) {
  const tone = TONES[domain];
  return <header className={cn('relative overflow-hidden rounded-card border border-line px-5 py-6 sm:px-7 sm:py-7', tone.surface)} data-id="element-5210" data-mp="183er5rjbxh26">
      <span aria-hidden className={cn('absolute inset-y-0 left-0 w-1', tone.rail)} data-id="element-5211" data-mp="1lt8geayqy3cf" />
      <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between" data-id="element-5212" data-mp="1gux4zvsszks3">
        <div className="min-w-0" data-id="element-5213" data-mp="1vrxyo4uk1pt7">
          <p className={cn('text-body-sm font-semibold', tone.eyebrow)} data-id="element-5214" data-mp="1vs7nvq7rzsxu">{eyebrow}</p>
          <h1 className="mt-1.5 text-h2 font-semibold tracking-tight text-ink lg:text-h1" data-id="element-5215" data-mp="1qufm84hbwsgg">
            {title}
          </h1>
          {description && <p className={cn('mt-3 text-body-lg text-ink-muted', tone.measure)} data-id="element-5216" data-mp="3slsriun7l2d1">{description}</p>}
          {meta && <div className="mt-4 flex flex-wrap items-center gap-2" data-id="element-5217" data-mp="282ysdqvmixzy">{meta}</div>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 lg:shrink-0" data-id="element-5218" data-mp="2d10aa8hbcr9u">{actions}</div>}
      </div>
    </header>;
}